import type { IResponse, IApiResponse } from "../@types/ApiResponse";
import { emptySplitApi } from "./emptySliceApi";
import endpoints from "./endpoints";

export const fileService = emptySplitApi.injectEndpoints({
    endpoints: (builder) => ({
        CreateFile: builder.mutation<IResponse<{ id: number, fileName: string }>, { file: File, caption?: string, isGlobalContent?: boolean, Duration?: number }>({
            query: ({ file, caption = '', Duration = null, isGlobalContent = false }) => {
                var fileContains: FormData = new FormData();
                fileContains.append("file", file);
                if (caption) {
                    fileContains.append("Caption", caption);
                }
                if (Duration !== undefined && Duration !== null) {
                    fileContains.append("Duration", Duration.toString());
                }
                fileContains.append("IsGlobalContent", isGlobalContent.toString());
                return {
                    url: endpoints.files.base,
                    method: "POST",
                    body: fileContains,
                };
            },
            invalidatesTags: ["File"],
        }),
        GetFiles: builder.query<Blob, { fileName: string }>({
            query: ({ fileName }) => ({
                url: endpoints.files.base + `?fileName=${fileName}`,
                responseHandler: async (response) => {
                    // Get content-type from headers
                    const contentType = response.headers.get('content-type') || 'application/octet-stream';
                    const blobData = await response.blob();
                    // Create blob with correct content type
                    return new Blob([blobData], { type: contentType });
                },
                headers: {
                    Accept: "*/*", // Ensures binary response
                },
            }),
            providesTags: ["File"],
        }),
        GetContentLibrary: builder.query<IApiResponse<Files.ComponentContentDto>, Files.ContentLibraryFilter>({
            query: (params) => {
                const queryParams = new URLSearchParams();

                if (params.searchText) queryParams.append('SearchText', params.searchText);
                if (params.componentType) queryParams.append('ComponentType', params.componentType);
                if (params.page) queryParams.append('Page', params.page.toString());
                if (params.pageSize !== undefined) queryParams.append('Offset', params.pageSize.toString());

                return {
                    url: `${endpoints.files.contentLibrary}?${queryParams.toString()}`
                }
            },
            providesTags: ['File'],
        }),
        deleteFile: builder.mutation<IResponse<null>, { id: number }>({
            query: ({ id }) => {
                return {
                    url: endpoints.files.base + "/" + id,
                    method: "DELETE",
                };
            },
            invalidatesTags: ["File"],
        }),
        UpdateFile: builder.mutation<IResponse<{ id: number, fileName: string }>, { id: number, file?: File, caption?: string }>({
            query: ({ id, file, caption }) => {
                var fileContains: FormData = new FormData();
                if (file) {
                    fileContains.append("file", file);
                }

                // Build query parameters
                const queryParams = new URLSearchParams();
                queryParams.append('id', id.toString());
                queryParams.append('IsGlobalContent', 'true');
                if (caption !== undefined) {
                    queryParams.append('Caption', caption);
                }

                return {
                    url: `${endpoints.files.update}?${queryParams.toString()}`,
                    method: "POST",
                    body: fileContains,
                };
            },
            invalidatesTags: ["File"],
        }),
        GetFileUsage: builder.query<IResponse<Files.FileUsage[]>, string>({
            query: (fileName) => {
                return {
                    url: `${endpoints.files.usage}?fileName=${encodeURIComponent(fileName)}`,
                    method: "GET",
                };
            },
            providesTags: ["File"],
        }),
    }),
});

export const {
    useCreateFileMutation,
    useGetFilesQuery,
    useLazyGetFilesQuery,
    useGetContentLibraryQuery,
    useDeleteFileMutation,
    useUpdateFileMutation,
    useGetFileUsageQuery,
} = fileService;
